import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { toast } from "react-toastify";

import NoResults from "../../../../Common/NoResults";
import TEMPLATE_TAGS_COLOR from "../../../../Common/Template/helper";
import PdfPreviewModal from "../../../../Common/UIComponents/PdfPreviewModal";
import {
  downloadCoaFromS3Paths,
  fetchDefaultCoaPreviewList,
  fetchEnvironmentTemplateDetail,
  fetchEnvironmentTemplates,
  generateEnvCoaPdfBlob,
  getEnvironmentProductFieldMap,
} from "./envCoaPreviewHelper";
import PreviewDatePicker from "./PreviewDatePicker";

import emptyStateIcon from "../../../../../assets/images/EmptyState_V2.png";
import { ReactComponent as RectangleLine } from "../../../../../assets/images/environment/RectangleLine.svg";

import "../../../../Common/Template/Components/Templates/TemplateCard.css";
import "./ReportPreviewModal.css";

const getSortedPreviewList = (previewUrlList) => (
  [...(previewUrlList || [])].sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
);

export default function ReportPreviewModal({
  previewUrlList: initialPreviewUrlList,
  previewContext,
  handleCloseForPreview,
}) {
  const [sortedUrlList, setSortedUrlList] = useState(() => getSortedPreviewList(initialPreviewUrlList));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeDate, setActiveDate] = useState(sortedUrlList[0]?.date || "");
  const [loadingDefaultPreview, setLoadingDefaultPreview] = useState(false);

  const [templateList, setTemplateList] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templatePreviewUrl, setTemplatePreviewUrl] = useState("");
  const [templatePreviewBlob, setTemplatePreviewBlob] = useState(null);
  const [defaultPreviewS3Paths, setDefaultPreviewS3Paths] = useState([]);
  const [loadingTemplatePreview, setLoadingTemplatePreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const defaultPreviewAbortRef = useRef(null);
  const templatePreviewAbortRef = useRef(null);

  const showTemplateSidebar = Boolean(previewContext?.report);

  useEffect(() => {
    let cancelled = false;
    const loadDefaultPreview = async () => {
      if (initialPreviewUrlList?.length) {
        const sorted = getSortedPreviewList(initialPreviewUrlList);
        setSortedUrlList(sorted);
        setActiveDate(sorted[0]?.date || "");
        return;
      }
      if (!previewContext?.path) {
        return;
      }
      if (defaultPreviewAbortRef.current) {
        defaultPreviewAbortRef.current.abort();
      }
      const controller = new AbortController();
      defaultPreviewAbortRef.current = controller;

      setLoadingDefaultPreview(true);
      try {
        const { previews, s3Paths } = await fetchDefaultCoaPreviewList({
          filePath: previewContext.path,
          activeDate: previewContext.activeDate,
          report: previewContext.report,
          signal: controller.signal,
        });
        if (!cancelled) {
          const sorted = getSortedPreviewList(previews);
          setSortedUrlList(sorted);
          setDefaultPreviewS3Paths(s3Paths);
          setActiveDate(sorted[0]?.date || previewContext.activeDate || "");
        }
      } catch (error) {
        if (!cancelled && !controller.signal.aborted) {
          toast.error(error.message || "Preview not available.");
        }
      }
      if (!cancelled) {
        setLoadingDefaultPreview(false);
      }
    };
    loadDefaultPreview();
    return () => {
      cancelled = true;
      if (defaultPreviewAbortRef.current) {
        defaultPreviewAbortRef.current.abort();
      }
    };
    // eslint-disable-next-line
  }, [initialPreviewUrlList, previewContext?.path, previewContext?.activeDate, previewContext?.report]);

  useEffect(() => {
    if (!showTemplateSidebar) {
      return undefined;
    }
    let cancelled = false;
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      const templates = await fetchEnvironmentTemplates();
      if (!cancelled) {
        setTemplateList(templates);
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [showTemplateSidebar]);

  const revokeTemplatePreviewUrl = useCallback(() => {
    if (templatePreviewUrl) {
      URL.revokeObjectURL(templatePreviewUrl);
    }
  }, [templatePreviewUrl]);

  useEffect(() => () => {
    revokeTemplatePreviewUrl();
  }, [revokeTemplatePreviewUrl]);

  useEffect(() => () => {
    if (defaultPreviewAbortRef.current) {
      defaultPreviewAbortRef.current.abort();
    }
    if (templatePreviewAbortRef.current) {
      templatePreviewAbortRef.current.abort();
    }
  }, []);

  const loadTemplatePreview = useCallback(async (templateId, signal) => {
    const template = await fetchEnvironmentTemplateDetail(templateId, signal);
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }
    if (!template) {
      return { success: false, message: "Could not fetch template." };
    }
    const productFieldMap = await getEnvironmentProductFieldMap();
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }
    const blob = await generateEnvCoaPdfBlob({
      template,
      report: previewContext.report,
      specsData: previewContext.specsData,
      activeDate: previewContext.activeDate,
      productFieldMap,
      signal,
    });
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }
    if (!blob) {
      return { success: false, message: "Failed to generate template preview." };
    }
    return {
      success: true,
      url: URL.createObjectURL(blob),
      blob,
    };
  }, [previewContext]);

  const handleTemplateSelect = async (templateId) => {
    if (templatePreviewAbortRef.current) {
      templatePreviewAbortRef.current.abort();
    }
    if (selectedTemplateId === templateId) {
      revokeTemplatePreviewUrl();
      setSelectedTemplateId(null);
      setTemplatePreviewUrl("");
      setTemplatePreviewBlob(null);
      setLoadingTemplatePreview(false);
      return;
    }

    const controller = new AbortController();
    templatePreviewAbortRef.current = controller;
    setLoadingTemplatePreview(true);
    revokeTemplatePreviewUrl();
    setTemplatePreviewUrl("");
    setTemplatePreviewBlob(null);
    setSelectedTemplateId(templateId);

    try {
      const result = await loadTemplatePreview(templateId, controller.signal);
      if (result.canceled || controller.signal.aborted) {
        return;
      }
      if (!result.success) {
        toast.error(result.message || "Failed to generate template preview.");
        setSelectedTemplateId(null);
      } else {
        setTemplatePreviewUrl(result.url);
        setTemplatePreviewBlob(result.blob);
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      toast.error(error.message || "Failed to generate template preview.");
      setSelectedTemplateId(null);
    } finally {
      if (templatePreviewAbortRef.current === controller) {
        setLoadingTemplatePreview(false);
      }
    }
  };

  const handleDateChanged = (date, index) => {
    setActiveDate(date);
    setCurrentSlide(index);
  };

  const activeDefaultPreview = sortedUrlList[currentSlide];
  const showingTemplatePreview = Boolean(selectedTemplateId && templatePreviewUrl);
  const previewBlob = showingTemplatePreview ? templatePreviewUrl : activeDefaultPreview?.url;
  const previewLoading = loadingDefaultPreview || loadingTemplatePreview;

  const modalTitle = showTemplateSidebar ? "Create a customized COA" : "COA";
  const previewFiles = previewBlob ? [{
    key: showingTemplatePreview ? "template" : (activeDefaultPreview?.date || "coa"),
    fileName: showingTemplatePreview
      ? `${previewContext?.report?.sample_id || "COA"}_COA.pdf`
      : (activeDefaultPreview?.name || "COA.pdf"),
    src: previewBlob,
  }] : [];

  const handleDownload = async () => {
    if (downloading) {
      return;
    }
    setDownloading(true);
    try {
      if (showingTemplatePreview && templatePreviewBlob) {
        const sampleId = previewContext?.report?.sample_id || "COA";
        const link = document.createElement("a");
        link.href = URL.createObjectURL(templatePreviewBlob);
        link.download = `${sampleId}_COA.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success("Successfully downloaded COA.");
      } else if (defaultPreviewS3Paths.length > 0) {
        const { totalFiles, filesDownloaded } = await downloadCoaFromS3Paths(defaultPreviewS3Paths);
        if (filesDownloaded === totalFiles && totalFiles > 0) {
          toast.success(`Successfully downloaded ${filesDownloaded}/${totalFiles} files.`);
        } else if (filesDownloaded > 0) {
          toast.warning(`Downloaded ${filesDownloaded}/${totalFiles} files.`);
        } else {
          toast.error("Failed to download COA.");
        }
      } else {
        toast.error("Unable to download, no valid file path for this report!");
      }
    } catch (error) {
      toast.error(error.message || "Failed to download COA.");
    }
    setDownloading(false);
  };

  const canDownload = useMemo(() => {
    if (showingTemplatePreview && templatePreviewBlob) {
      return true;
    }
    if (defaultPreviewS3Paths.length > 0) {
      return true;
    }
    return sortedUrlList.some((item) => item.url);
  }, [showingTemplatePreview, templatePreviewBlob, defaultPreviewS3Paths, sortedUrlList]);

  return (
    <PdfPreviewModal
      open
      title={modalTitle}
      onClose={handleCloseForPreview}
      files={previewFiles}
      loading={previewLoading}
      showDownload={false}
      showPrint={false}
      errorMessage="Preview not available."
      width={showTemplateSidebar ? "60vw" : "40vw"}
      className={`ReportPreviewModal${showTemplateSidebar ? " ReportPreviewModal--customized" : ""}`}
      headerExtra={!showingTemplatePreview && sortedUrlList.length > 1 ? (
        <PreviewDatePicker
          data={sortedUrlList}
          activeDate={activeDate}
          updateActiveDate={handleDateChanged}
        />
      ) : null}
      extra={showTemplateSidebar ? (
        <div className="ReportPreviewModal__TemplateSidebar">
          <div className="ReportPreviewModal__TemplateSidebarTitle">
            <RectangleLine aria-hidden />
            <span>Choose Templates</span>
          </div>
          <div className="ReportPreviewModal__TemplateList">
            {loadingTemplates && <div className="ReportPreviewModal__TemplateLoading ActiveloadingAnimation" />}
            {!loadingTemplates && templateList.map((template) => {
              const tagStyle = TEMPLATE_TAGS_COLOR[template.tags?.[0]] || TEMPLATE_TAGS_COLOR.blue;
              const isSelected = selectedTemplateId === template.template_id;
              return (
                <button
                  type="button"
                  key={template.template_id}
                  className={`TemplateChooseItem ReportPreviewModal__TemplateItem${isSelected ? " TemplateChooseItem--selected" : ""}`}
                  onClick={() => handleTemplateSelect(template.template_id)}
                >
                  <span
                    className="TemplateChooseItem__Accent"
                    style={{ backgroundColor: tagStyle.backgroundColor }}
                  />
                  <span className="TemplateChooseItem__Name" title={template.name}>{template.name}</span>
                </button>
              );
            })}
            {!loadingTemplates && templateList.length === 0 && (
              <NoResults image={emptyStateIcon} message="No templates available." />
            )}
          </div>
          {canDownload && (
            <Button
              type="primary"
              className="ReportPreviewModal__SidebarDownloadButton"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownload}
            >
              Download
            </Button>
          )}
        </div>
      ) : null}
    />
  );
}
